import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useTheme } from '@/hooks/useTheme'

/**
 * Registro con GoTrue MapeoData (DEC-003).
 * Identidad visual "Cabina de inteligencia" (Fase 3) — la lógica de registro,
 * el manejo de error y el estado de confirmación por email se conservan.
 * Tras registro exitoso → email de verificación + redirect a /login.
 */
export default function Register() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
      })
      if (authError) {
        setError(authError.message)
        return
      }
      setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  const ThemeToggle = (
    <button
      type="button"
      className="icon-btn auth-theme"
      onClick={toggleTheme}
      title="Cambiar tema"
      aria-label="Cambiar tema"
    >
      {theme === 'dark' ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
        </svg>
      )}
    </button>
  )

  if (done) {
    return (
      <div className="cabina-root">
        <div className="auth-screen">
          {ThemeToggle}
          <div className="auth-card auth-success">
            <div className="as-ic">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" />
              </svg>
            </div>
            <h1>Revisa tu email</h1>
            <p className="auth-sub" style={{ marginBottom: 22 }}>
              Te hemos enviado un enlace para confirmar tu cuenta. Una vez verifiques,
              podrás iniciar sesión.
            </p>
            <button onClick={() => navigate('/login')} className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}>
              Ir al inicio de sesión
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="cabina-root">
      <div className="auth-screen">
        {ThemeToggle}
        <div className="auth-card">
          <Link to="/" className="auth-brand">
            <span className="mark">M</span>MapeoData
          </Link>
          <h1>Crear cuenta gratis</h1>
          <p className="auth-sub">Empieza con 15 leads al mes. Sin tarjeta.</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="cab-field" style={{ margin: 0 }}>
              <label htmlFor="reg-email">Email</label>
              <input
                id="reg-email"
                className="cab-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </div>
            <div className="cab-field" style={{ margin: 0 }}>
              <label htmlFor="reg-pass">Contraseña</label>
              <input
                id="reg-pass"
                className="cab-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
              />
            </div>
            {error && (
              <div className="auth-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
                </svg>
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn btn-accent">
              {loading ? 'Creando cuenta…' : 'Crear cuenta gratis'}
            </button>
          </form>

          <p className="auth-foot">
            ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
